<template lang="pug">
.appointment-calendar-middle-panel.d-flex.flex-column.fill-height.overflow-hidden
  //- Top navbar
  .py-5.px-md-4
    .d-flex.justify-space-between.align-center
      //- Date selection
      .d-flex.align-center
        //- Filter button (mobile only)
        v-btn.button-xs.mr-2(v-if="isMobileView" icon small @click="$emit('toggleLeftPanel')")
          v-icon(size="20" color="neutral700") $yzi-menu-05
        .font-medium(v-if="cal" :class="[isMobileView ? 'text-sm mr-1' : 'text-xl mr-6']") {{ getTitle }}
        .d-flex.gap-1
          v-btn.button-xs(text small @click="prev")
            v-icon(size="20" color="neutral700") mdi-chevron-left
          v-btn.button-xs(text small @click="next")
            v-icon(size="20" color="neutral700") mdi-chevron-right
      //- Right hand actions
      .d-flex.align-center.gap-2.flex-wrap
        //- Today
        v-btn.button-sm(text @click="setToday")
          .text-sm.font-normal Today
        //- View selection
        v-menu(offset-y bottom)
          template(v-slot:activator='{ on, attrs }')
            v-btn.button-sm.mr-2(text v-on="on")
              v-icon.mr-2(v-if="!isMobileView" size="15" color="neutral500") {{ selectedType.icon }}
              .text-sm.font-normal.mr-2(v-if="!isMobileView") {{ selectedType.label }}
              .text-sm.font-normal.mr-2(v-else) {{ selectedType.char }}
              v-icon(size="15") mdi-chevron-down
          v-list
            v-list-item.hover-bg(v-for="type in types" :key="type.value" @click="onSelectedType(type)")
              v-list-item-title.type-list-item
                .d-flex.align-center.justify-space-between.px-4.py-3
                  div.d-flex.align-center
                    v-icon.mr-3(size="15" color="neutral500") {{ type.icon }}
                    .text-sm {{ type.label }}
                  .text-sm.neutral300.neutral500--text.rounded-lg.type-char-item.d-flex.justify-center.align-center {{ type.char }}
                v-divider
        //- Add appointment
        v-btn.button-sm.white--text(depressed color="neutral700" @click="openAddAppointmentDialog = true")
          v-icon(:class="{ 'mr-2 ml-n2': !isMobileView }" size="18") $yzi-plus
          .text-sm(v-if="!isMobileView") Add
  //- Calendar
  v-sheet.flex-grow-1.overflow-y-auto.pb-2
    template(v-if="isFetching")
      v-progress-linear(color="primary" indeterminate)
    template(v-else-if="isError")
      div.danger500.my-1.px-4.py-2.text-sm.white--text.font-semibold.text-center An error occured. Please try again
    v-calendar(ref="calendar" v-model="focus" :events="events" color="primary" :weekdays="weekdays" :type="selectedType.value" :categories="categories" :weekday-format="formatWeekday" category-show-all :show-month-on-first="false" interval-height="20" interval-count="96" :interval-minutes="intervalMinutes" event-more-text="{0} more..." @click:event="onClickEvent" @change="onDateChange" @click:date="onDateSelected" @mousemove:time="onHoverToSeeTime" @mousemove:time-category="onHoverToSeeTimeCategory" @mouseleave:time="onRemoveHover" @mouseleave:time-category="onRemoveHover" @click:time="onResetSelectedEvent" @click:day="onResetSelectedEvent" @click:more="onClickMore")
      //- Removing day label header
      template(#day-label-header)
        div
      template(#category="{ category }")
        .d-flex.flex-column.align-center.gap-2.mt-2
          v-avatar(color="neutral300" size="16" rounded="circle")
            v-img(v-if="getProviderImage(category)" width="16" height="16" :src="getProviderImage(category)")
            v-icon(v-else size="13") $yzi-user-03
          .text-xs {{ category }}
      template(#event="{ event }")
        div.fill-height.pl-1.event-overflow(:class="[{ 'event-transparent': !isFutureEvent(event) }, { 'event-is-active': isSelectedEvent(event.details.id) }]")
          div.fill-height.pl-1(:class="getEventStyle(event)")
            div.truncate {{ displayEventTime(event.startDateTime) }}&nbsp;
              span.font-semibold {{ event.name }}
            template(v-if="event.durationInMinutes > 25")
              div.truncate {{ event.serviceName }}
              div.italic.truncate {{ event.notes }}
      template(#day-body="{ date, week, timeToY, category }")
        template(v-if="showCurrentTimeLine")
          .v-current-time(:style="{ top: nowY }")
          .text-xxs.white--text.round-md(v-if="date === week[0].date" :class="[selectedType.value === 'category' ? 'time-show-providers' : 'time-show']" :style="{ top: newYTime }") {{ currentTime }}
      //- TODO fix this error when screen adjustment
      //- template(#interval="{ date, hour, minute, category }")
      //-   div.interval-height.clickable(v-if="isHovering(category, date, hour, minute)" @click="onClickInterval")
      //-     .text-xxs.fill-height.d-flex.align-center.info100.pl-1 {{ hoverTime }}
  //- Dialogs
  .dialogs
    add-appointment(v-if="openAddAppointmentDialog" :open.sync="openAddAppointmentDialog" @reset="fetchAppointments" @advance="openAdvanceAppointment()")
    add-advance-appointment(v-if="openAdvAppointmentDialog" :open.sync="openAdvAppointmentDialog" @reset="fetchAppointments")
</template>

<script>
import { DateTime, Interval } from 'luxon'
import { mapGetters, mapActions } from 'vuex'
import AddAppointment from '@/components/clinic/dialogs/AddAppointment.vue'
import AddAdvanceAppointment from '@/components/clinic/dialogs/AddAdvanceAppointment.vue'

export default {
  components: {
    AddAppointment,
    AddAdvanceAppointment,
  },
  props: {
    selectedId: {
      type: Number,
      default: null
    },
    selectedProviders: {
      type: Array,
      default: () => {
        return []
      }
    },
    isMobileView: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      interval: null,
      isFetching: true,
      isError: false,
      ready: false,
      selectedType: {
        label:'Day',
        icon: '$yzi-layout-top',
        value: 'day',
        char: 'D'
      },
      types: [
        {
          label:'Day',
          icon: '$yzi-layout-top',
          value: 'day',
          char: 'D'
        },
        {
          label:'Week',
          icon: '$yzi-columns-03',
          value: 'week',
          char: 'W'
        },
        {
          label:'Month',
          icon: '$yzi-grid-01',
          value: 'month',
          char: 'M'
        },
        {
          label:'Provider',
          icon: '$yzi-user-03',
          value: 'category',
          char: 'P'
        },
        {
          label: 'List',
          icon: '$yzi-list',
          value: 'list',
          char: 'L'
        }
      ],
      focus: '',
      hoverDateTime: null,
      events: [],
      selectedEvent: null,
      initialized: false,
      openAddAppointmentDialog: false,
      openAdvAppointmentDialog: false,
      intervalMinutes: 15,
      hoverCategory: "",
      isSelecting: false,
      weekdays: [1, 2, 3, 4, 5, 6, 0],
      weekdayToDayMapper: {
        0: 'Sun',
        1: 'Mon',
        2: 'Tue',
        3: 'Wed',
        4: 'Thu',
        5: 'Fri',
        6: 'Sat',
      }
    }
  },
  mounted () {
    this.ready = true
    this.fetchAppointments()
    this.scrollToTime()
    this.updateTime()
  },
  beforeDestroy () {
    clearInterval(this.interval)
  },
  watch: {
    selectedId (value) {
      if (!value) {
        this.selectedEvent = null
      }
    },
    selectedProviderIds () {
      this.fetchAppointments()
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      isHq: 'hq/getIsHq',
    }),
    cal () {
      return this.ready ? this.$refs.calendar : null
    },
    nowY () {
      return this.cal ? this.cal.timeToY(this.cal.times.now) + 'px' : '-10px'
    },
    newYTime () {
      return this.cal ? (this.cal.timeToY(this.cal.times.now) - 9) + 'px' : '-10px'
    },
    selectedProviderIds () {
      return this.selectedProviders.map(provider => provider.id)
    },
    params () {
      let params = {
        page_size: -1,
        statuses: 'new,processed,received,absent',
        providers: this.selectedProviderIds.length > 0 ? this.selectedProviderIds.join(',') : null
      }

      switch (this.selectedType?.value) {
        case 'day':
        case 'category':
          params.date = this.cal.lastStart.date
          break

        case 'week':
        case 'month':
          params.date__gte = this.cal.lastStart.date
          params.date__lte = this.cal.lastEnd.date
          break

        default:
          break
      }

      return params
    },
    categories () {
      return this.selectedProviders.map((provider) => provider.label)
    },
    currentDateTime () {
      if (!this.cal) return null

      return DateTime.fromISO(`${this.cal.times.now.date}T${this.cal.times.now.time}:00`)
    },
    currentTime () {
      if (!this.currentDateTime) return null

      return this.currentDateTime.toFormat('hh:mm a')
    },
    showCurrentTimeLine () {
      if (this.cal) {
        if (this.selectedType.value === 'week') {
          const startDateTime = DateTime.fromISO(`${this.cal.lastStart.date}T00:00:00`)
          const endDateTime = DateTime.fromISO(`${this.cal.lastEnd.date}T00:00:00`)
          return startDateTime <= this.currentDateTime && endDateTime >= this.currentDateTime
        }

        if (this.selectedType.value === 'day' || this.selectedType.value === 'category') {
          return this.cal.lastStart.date === this.currentDateTime.toFormat('yyyy-MM-dd')
        }
      }

      return false
    },
    hoverTime () {
      if (this.hoverDateTime) {
        return this.hoverDateTime.toFormat('hh:mm a')
      }

      return null
    },
    getTitle () {
      if (!this.cal) return null

      if (this.selectedType.value === 'day' || this.selectedType.value === 'category') {
        return `${this.cal.lastStart.day} ${this.cal.title}`
      }

      return this.cal.title
    }
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    setToday () {
      this.focus = ''
    },
    prev () {
      this.cal.prev()
    },
    next () {
      this.cal.next()
    },
    getCurrentTime () {
      return this.cal ? this.cal.times.now.hour * 60 + this.cal.times.now.minute : 0
    },
    scrollToTime (datetime) {
      let time
      
      if (datetime) {
        time = datetime.hour * 60 + datetime.minute
      } else {
        time = this.getCurrentTime()
      }

      const first = Math.max(0, time - (time % 30) - 30)
      
      this.cal.scrollToTime(first)
    },
    updateTime () {
      this.interval = setInterval(() => this.cal.updateTimes(), 60 * 1000)
    },
    async fetchAppointments () {
      try {
        if (this.selectedProviderIds.length === 0) {
          this.events = []
          return
        }
        
        this.isFetching = true

        const endpoint = this.isHq ? `/clinics/${this.company.slug}/hq/appointments` :  `/clinics/${this.company.slug}/appointments`
        const { data } = await this.$axios.get(endpoint, { params: this.params })
        if (data.results) {
          this.events = data.results.map(result => this.sanitizeData(result))
        } else {
          this.events = []
        }
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isError = true
      } finally {
        this.isFetching = false
      }
    },
    sanitizeData (obj) {
      const newObj = { details: { ...obj } }
      // Name
      newObj.name = obj.lead.name
      // Date time
      const format = 'yyyy-MM-d HH:mm'
      const startDateTime = DateTime.fromISO(obj.start)
      const endDateTime = DateTime.fromISO(obj.until)
      newObj.startDateTime = startDateTime
      newObj.start = startDateTime.toFormat(format)
      newObj.endDateTime = endDateTime
      newObj.end = endDateTime.toFormat(format)
      // Duration (in minutes)
      newObj.durationInMinutes = Interval.fromDateTimes(startDateTime, endDateTime).toDuration('minutes').toObject().minutes
      // Service
      if (obj.type !== 'combo') {
        newObj.serviceName = obj?.services.length ? obj.services.map(service => service.name).join(', ') : null
      } else {
        newObj.serviceName = obj?.combo_set.length ? obj.combo_set.map(set => set?.services[0]?.name || '').join(', ')  : null
      }
      // Color
      newObj.color = obj?.provider?.extra_data?.tag_color || '#333333'
      // Category
      newObj.category = obj?.provider?.name || null
      // Notes
      newObj.notes = obj.lead?.remarks || null

      return newObj
    },
    displayEventTime (datetime) {
      return DateTime.fromISO(datetime).toFormat('hh:mm')
    },
    onClickEvent ({event}) {
      this.isSelecting = true
      this.selectedEvent = event
      this.$store.commit('hq/setSelectedCompany', event.details.company)
      this.$emit('update:selectedId', event.details.id)
    },
    isSelectedEvent (id) {
      if (this.selectedId) {
        return this.selectedId === id
      }

      return false
    },
    onResetSelectedEvent () {
      if (this.isSelecting) {
        this.isSelecting = false
        return
      }
      this.selectedEvent = null
      this.$emit('update:selectedId', null)
    },
    isFutureEvent (event) {
      return event.endDateTime >= this.currentDateTime
    },
    onDateChange (e) {
      this.$nextTick(() => {
        this.injectTodayButtonLabelStyling()
      })
      // Prevent api call on first initialization since lastStart and lastEnd data is not ready yet in params
      // https://v2.vuetifyjs.com/en/api/v-calendar/#events
      if (!this.initialized) {
        this.initialized = true
        return
      }
      this.fetchAppointments()
      this.$emit('dateChanged', e)
    },
    onDateSelected () {
      this.onResetSelectedEvent()
      switch (this.selectedType.value) {
        case 'week': {
          const index = this.types.findIndex(type => type.value === 'day')
          this.selectedType = this.types[index]
          break
        }

        case 'month': {
          const index = this.types.findIndex(type => type.value === 'week')
          this.selectedType = this.types[index]
          break
        }

        default:
          break
      }
    },
    onHoverToSeeTime ({ date, time }) {
      const timeDigit = time.split(":");
      const minutes = Number(timeDigit[1]);
      const start = this.findStart(minutes);
      const timeString = `${timeDigit[0]}:${start}`
      this.hoverDateTime = DateTime.fromISO(`${date}T${timeString}`)
    },
    onHoverToSeeTimeCategory ({ date, time, category }) {
      const timeDigit = time.split(":");
      const minutes = Number(timeDigit[1]);
      const start = this.findStart(minutes);
      const timeString = `${timeDigit[0]}:${start}`
      this.hoverDateTime = DateTime.fromISO(`${date}T${timeString}`)
      this.hoverCategory = category
    },
    onRemoveHover () {
      this.hoverDateTime = null
    },
    isHovering (category, date, hour, minute) {
      const hourString = `${hour}`.padStart(2, '0')
      const minuteString = `${minute}`.padStart(2, '0')
      const time = `${hourString}:${minuteString}`
      const hoverTime = DateTime.fromISO(this.hoverDateTime).toFormat('HH:mm')
      const hoverDate = DateTime.fromISO(this.hoverDateTime).toFormat('yyyy-MM-dd')

      if (this.selectedType.value === 'category') {
        return category === this.hoverCategory && date === hoverDate && time === hoverTime
      }

      return date === hoverDate && time === hoverTime
    },
    findStart (minutesDigit) {
      const start = minutesDigit - (minutesDigit % this.intervalMinutes)
      return `${start}`.padStart(2, '0')
    },
    async onSelectedType (type) {
      if (type.value === 'list') {
        if (this.isHq) {
          await this.$store.commit('hq/setSelectedAppointmentView', 'list')
          this.$router.push('/admin/hq/bookings')
          return
        }
        await this.$store.commit('clinic/setSelectedAppointmentView', 'list')
        this.$router.push('/admin/clinic/appointment')
        return
      }

      this.selectedType = type
    },
    getEventStyle (event) {
      if (this.isSelectedEvent(event.details.id)) {
        if (event.color === '#FEC84D') {
          return 'neutral700--text'
        }

        return 'white--text'
      }

      if (this.isFutureEvent(event)) return 'neutral700--text event-background-transparent'

      return 'neutral500--text event-background-transparent'
    },
    onClickMore ({ date }) {
      this.focus = date
      this.selectedType = this.types.find(type => type.value === 'day')
    },
    formatWeekday ({ weekday, day }) {
      let text = `${this.weekdayToDayMapper[weekday]}`
      if (this.selectedType.value !== 'month') {
        text = `${text} ${day}`
      }
      return text
    },
    injectTodayButtonLabelStyling () {
      // This shit will inject span of today date button styling for the selected html
      const element = this.$el.querySelector('.v-calendar-daily_head-day.v-present .v-calendar-daily_head-weekday')
      // Return early if null
      if (!element) return
      // Process innerHTML
      const todayDate = this.currentDateTime.day
      const todayDay = this.weekdayToDayMapper[this.currentDateTime.weekday]
      element.innerHTML = `${todayDay} <span class='today__button'>${todayDate}</span>`
    },
    getProviderImage (name) {
      // Using name only to get provider image due to damn vuetify not returning category as object. I don't understand why they make developer's life harder
      const provider = this.selectedProviders.find(provider => provider.label === name)
      return provider.image?.file || null
    },
    onClickInterval () {
      if (this.selectedId) return

      this.openAddAppointmentDialog = true
    },
    openAdvanceAppointment () {
      this.openNewAppointmentDialog = false
      this.openAdvAppointmentDialog = true
    },
  },
}
</script>

<style lang="scss" scoped>
.appointment-calendar-middle-panel {
  height: 100%;
  min-height: 0;
}

::v-deep .v-current-time {
  height: 1px;
  background-color: #000;
  position: absolute;
  left: -1px;
  right: 0;
  pointer-events: none;
}

::v-deep .time-show,
::v-deep .v-calendar-daily__day-container .v-calendar-daily__day:nth-child(2) .v-calendar-category__columns .v-calendar-category__column .time-show-providers {
  position: absolute;
  background-color: #000;
  left: -53px;
  padding: 2px 4px;
  pointer-events: none;
  z-index: 5;
}

::v-deep .v-calendar-daily__day-interval {
  border-top: 1px solid var(--v-neutral400) !important;
}

::v-deep .v-calendar-daily__interval::after {
  border-top: none !important;
}

::v-deep .v-calendar-daily__intervals-body {
  border-right: 1px solid var(--v-neutral400) !important;
}

::v-deep .v-calendar-daily__intervals-body .v-calendar-daily__interval:not(.v-calendar-daily__interval:nth-child(4n + 1)) .v-calendar-daily__interval-text  {
  display: none;
}

::v-deep .v-calendar-daily__day .v-calendar-daily__day-interval:not(.v-calendar-daily__day-interval:nth-child(4n + 1)) {
  border-top: solid 1px var(--v-neutral300) !important;
}

::v-deep .v-calendar-daily,
::v-deep .v-calendar-weekly {
  border-left: none !important;
}

.event-overflow {
  overflow-y: hidden;
}

.event-background-transparent {
  background-color: rgba(255, 255, 255, 0.5);
}

.event-transparent {
  background-color: rgba(255, 255, 255, 0.5);
}

::v-deep .interval-height {
  height: 20px;
}

.type-char-item {
  height: 24px;
  width: 24px;
}

.type-list-item {
  width: 200px;
}

::v-deep .v-list,
::v-deep .v-list-item {
  padding: 0 !important;
}

.event-is-active {
  box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.2) !important;
}

::v-deep .v-event-more {
  margin-left: 4px;
  /* From figma style */
  overflow: hidden;
  color: var(--neutral-500, #848484);
  text-overflow: ellipsis;

  /* Regular Body/Regular XXS */
  font-family: Inter;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 160% */
}

::v-deep .v-calendar-weekly__day-label {
  text-align: right;
}

::v-deep .v-calendar-weekly__week .v-calendar-weekly__day.v-present .v-calendar-weekly__day-label button.v-btn--fab.primary {
  height: 34px;
  width: 30px;
  border-radius: 8px;
  margin-right: 2px;
  margin-top: 4px;
}

::v-deep .v-calendar-weekly__week .v-calendar-weekly__day .v-calendar-weekly__day-label button.v-btn--fab.primary span {
  font-size: 10px;
  line-height: 16px
}

::v-deep .v-calendar-weekly__week .v-calendar-weekly__day.v-present .v-calendar-weekly__day-label button.v-btn--fab.primary span {
  font-weight: 700;
}

::v-deep .v-calendar-daily_head-weekday {
  text-transform: capitalize !important;
}

::v-deep .today__button {
  border-radius: 4px;
  padding: 0px 4px;
  background: var(--v-neutral700);
  color: var(--v-neutral100);
  font-size: 11px;
  font-weight: 500;
}

::v-deep .theme--light.v-calendar-weekly .v-calendar-weekly__head-weekday {
  border-bottom: 1px solid #e0e0e0;
}
::v-deep .theme--light.v-calendar-daily .v-calendar-daily_head-day,
::v-deep .theme--light.v-calendar-daily .v-calendar-daily__intervals-head,
::v-deep .theme--light.v-calendar-weekly .v-calendar-weekly__head-weekday,
::v-deep .theme--light.v-calendar-category .v-calendar-category__column-header {
  border-right: none;
}

::v-deep .theme--light.v-calendar-weekly .v-calendar-weekly__head-weekday.v-outside {
  background-color: transparent;
}
/* ::v-deep .theme--light.v-calendar-daily .v-calendar-daily_head-day.v-past .v-calendar-daily_head-weekday, .theme--light.v-calendar-daily .v-calendar-daily_head-day.v-past .v-calendar-daily_head-day-label {
  color: inherit;
} */
</style>